// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getPromotionsDataView(state, state.pages.orderConfirmation.cartId);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        cartState: getCartState(state, state.pages.orderConfirmation.cartId),
        orderPromotions,
        shippingPromotions,
        discountTotal,
        promotionsDataView,
        settingsCollection: getSettingsCollection(state),
        wishListSettings: getSettingsCollection(state).wishListSettings,
        enableVat: getSettingsCollection(state).productSettings.enableVat, // ! Check whether this one is necessary, or the other vat variable below
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderConfirmationTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    clickableStyles?: ClickablePresentationProps;
    addToListButton?: LinkPresentationProps;
}

export const orderConfirmationTotalStyles: OrderConfirmationTotalStyles = {
    clickableStyles: {
        css: css`
            color: ${getColor("secondary")};
            font-size: 15px;
            font-weight: 400;
            letter-spacing: 0.55px;
            line-height: 22.5px;

            &:hover,
            &:focus {
                text-decoration: underline;
            }
        `,
    },
    addToListButton: linkIconStyleGenerator("AddToList", "15px", 14, "", "", "margin-top: 30px;"),
};

const styles = orderConfirmationTotalStyles;

const OrderConfirmationTotal: FC<Props> = props => {
    if (!props.cartState.value || !props.promotionsDataView.value || !props.wishListSettings) {
        return null;
    }

    const dispatch = useDispatch();

    const { cartState, history, wishListSettings, setAddToListModalIsOpen, addToWishList } = props;

    const toasterContext = React.useContext(ToasterContext);

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    React.useEffect(() => {
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "breadcrumbActions",
                value: (
                    <Clickable
                        aria-label={translate("Print")}
                        color="secondary"
                        onClick={printOrOpenPrintAllModal}
                        {...styles.clickableStyles}
                    >
                        <Icon
                            src="Print"
                            size={15}
                            color="secondary"
                            css={css`
                                margin-right: 4px;
                            `}
                        ></Icon>
                        {translate("Print")}
                    </Clickable>
                ),
            },
        });
        return () => {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: null,
                },
            });
        };
    }, []);

    const addToListHandler = () => {
        if (!props.wishListSettings || !props?.cartState?.value?.cartLines) {
            return;
        }

        const productInfos = props?.cartState?.value?.cartLines.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        })) as Omit<ProductInfo, "productDetailPath">[];

        if (!props.wishListSettings.allowMultipleWishLists) {
            props.addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }
        props.setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
            `}
        >
            <CartTotalDisplay
                cart={props.cartState.value}
                orderPromotions={props.orderPromotions}
                discountTotal={props.discountTotal}
                shippingPromotions={props.shippingPromotions}
                extendedStyles={styles.cartTotal}
                // XNG-Changes
                {...{ showTaxMessage: true }}
            />
            <Link
                {...styles.addToListButton}
                onClick={addToListHandler}
                data-test-selector="orderConfirmation_addToList"
            >
                {translate("Add All to Project List")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderConfirmationTotal)),
    definition: {
        group: "Order Confirmation",
        allowedContexts: ["OrderConfirmationPage"],
        displayName: "Order Confirmation Total",
    },
};

export default widgetModule;
